import { createI18n } from "vue-i18n";

import en from "./locales/en";
import ru from "./locales/ru";
import uk from "./locales/uk";

export type AppLocale = "uk" | "ru" | "en";
export type MessageSchema = typeof uk;

/**
 * Глобальна типобезпека ключів `t()` (ADR-0003 D9): augmentation
 * `DefineLocaleMessage` змушує vue-i18n валідувати шляхи ключів у будь-якому
 * `useI18n()`/`$t` за формою `uk`. Друкарська помилка в ключі = compile error.
 * (Дженерик `createI18n` нижче фіксує лише форму `messages`, не ключі `t()`.)
 */
declare module "vue-i18n" {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- канонічний vue-i18n augmentation: інтерфейс лише підмінює форму DefineLocaleMessage схемою uk
  interface DefineLocaleMessage extends MessageSchema {}
}

/**
 * CLDR plural rule for Slavic languages (uk, ru):
 *   one  (index 0): ends in 1 but not 11 → «послуга / услуга»
 *   few  (index 1): ends in 2-4 but not 12-14 → «послуги / услуги»
 *   many (index 2): everything else → «послуг / услуг»
 */
function slavicPluralRule(choice: number): number {
  const mod10 = choice % 10;
  const mod100 = choice % 100;
  if (mod10 === 1 && mod100 !== 11) return 0;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 1;
  return 2;
}

/**
 * i18n-плюмбінг адмінки (ADR-0003 D9): RU default + fallback, каталоги UA/RU/EN.
 * Composition API (`legacy: false`) — компоненти беруть `t()` через `useI18n()`.
 * Дженерик `<{ message: MessageSchema }, AppLocale>` прив'язує форму каталогів
 * і набір локалей. Мовоперемикач-віджет — окремий follow-up.
 * 2026-08-02: дефолтну локаль змінено з UK на RU за продуктовим рішенням.
 */
export const i18n = createI18n<{ message: MessageSchema }, AppLocale>({
  legacy: false,
  globalInjection: true,
  locale: "ru" satisfies AppLocale,
  fallbackLocale: "ru" satisfies AppLocale,
  messages: { uk, ru, en },
  pluralRules: {
    uk: slavicPluralRule,
    ru: slavicPluralRule,
  },
});
